import { useMemo, useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { FindingSeverity, Inspection, InspectionFinding, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const OFFICER_ROLES = ['FIRE_INSPECTOR', 'CHIEF_FIRE_OFFICER'];
const SEVERITIES: FindingSeverity[] = ['MINOR', 'MODERATE', 'MAJOR', 'CRITICAL'];

/** Fire & Rescue — the inspector worklist: record findings and PASS/DEFECTS results (M06). */
export function Inspections() {
  const { persona } = useAuth();
  const isOfficer = persona ? persona.roles.some((r) => OFFICER_ROLES.includes(r)) : false;
  const [status, setStatus] = useState('BOOKED');
  const [type, setType] = useState('');
  const [mineOnly, setMineOnly] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (type) params.set('type', type);
    if (mineOnly) params.set('mine', 'true');
    return `/api/v1/m06/inspections?${params.toString()}`;
  }, [status, type, mineOnly]);

  const listQ = useApiQuery<Page<Inspection>>(path);

  return (
    <div>
      <PageHeader
        title="Inspections"
        subtitle="Officer worklist — book at an application or premises, then record findings and a result here (M06)."
        actions={
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1.5 text-sm text-slate-600">
              <input type="checkbox" checked={mineOnly} onChange={(e) => setMineOnly(e.target.checked)} /> Mine only
            </label>
            <select className="field-input !w-auto" value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">All types</option>
              <option value="INITIAL">Initial</option>
              <option value="ANNUAL">Annual</option>
              <option value="COMPLAINT">Complaint</option>
            </select>
            <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              <option value="BOOKED">Booked</option>
              <option value="COMPLETED">Completed</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>
        }
      />

      {!isOfficer ? <RoleNotice roles={OFFICER_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No inspections match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Type</th>
                <th className="table-head">Scheduled for</th>
                <th className="table-head">Inspector</th>
                <th className="table-head">Result</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((i) => (
                <tr key={i.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(i.id)}>
                  <td className="table-cell font-medium">{i.type}</td>
                  <td className="table-cell text-xs text-slate-500">{formatDateTime(i.scheduledFor)}</td>
                  <td className="table-cell font-mono text-xs">{i.inspectorId ?? '—'}</td>
                  <td className="table-cell">{i.result ? <StatusBadge status={i.result} /> : '—'}</td>
                  <td className="table-cell">
                    <StatusBadge status={i.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId ? (
        <InspectionDetailModal
          id={selectedId}
          canAct={isOfficer}
          onClose={() => setSelectedId(null)}
          onChanged={() => {
            setSelectedId(null);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function InspectionDetailModal({
  id,
  canAct,
  onClose,
  onChanged,
}: {
  id: string;
  canAct: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const q = useApiQuery<Inspection>(`/api/v1/m06/inspections/${id}`);
  const i = q.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<'PASS' | 'DEFECTS' | null>(null);
  const [findings, setFindings] = useState<InspectionFinding[]>([]);
  const [notes, setNotes] = useState('');

  const addFinding = () => setFindings((f) => [...f, { code: '', severity: 'MINOR', description: '' }]);
  const updateFinding = (idx: number, patch: Partial<InspectionFinding>) =>
    setFindings((f) => f.map((row, i2) => (i2 === idx ? { ...row, ...patch } : row)));
  const removeFinding = (idx: number) => setFindings((f) => f.filter((_, i2) => i2 !== idx));

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m06/inspections/${id}/result`, {
        result,
        findings: findings.filter((f) => f.code.trim()).map((f) => ({ ...f, description: f.description?.trim() || undefined })),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to record result');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={i ? `${i.type} inspection` : 'Inspection'} onClose={onClose} wide>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {i ? (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="Status" value={<StatusBadge status={i.status} />} />
            <Detail label="Type" value={i.type} />
            <Detail label="Scheduled for" value={formatDateTime(i.scheduledFor)} />
            {i.result ? <Detail label="Result" value={<StatusBadge status={i.result} />} /> : null}
            {i.inspectedAt ? <Detail label="Inspected at" value={formatDateTime(i.inspectedAt)} /> : null}
            {i.reinspectionDue ? <Detail label="Reinspection due" value={formatDateTime(i.reinspectionDue)} /> : null}
          </div>

          {i.notes ? <Detail label="Notes" value={i.notes} /> : null}
          {i.findings.length > 0 ? (
            <div>
              <div className="field-label">Recorded findings</div>
              <ul className="space-y-1 text-sm">
                {i.findings.map((f, idx) => (
                  <li key={idx} className="flex items-center gap-2">
                    <StatusBadge status={f.severity} /> <span className="font-medium">{f.code}</span>
                    {f.description ? <span className="text-slate-500">— {f.description}</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {canAct && i.status === 'BOOKED' ? (
            <div className="space-y-3 rounded-lg border border-slate-200 p-3">
              <div className="text-sm font-semibold text-slate-700">Record findings + result</div>
              <div className="flex gap-2">
                <button className={`btn-success ${result === 'PASS' ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setResult('PASS')}>
                  Pass
                </button>
                <button className={`btn-danger ${result === 'DEFECTS' ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setResult('DEFECTS')}>
                  Defects
                </button>
              </div>

              {result ? (
                <>
                  <div className="space-y-2">
                    {findings.map((f, idx) => (
                      <div key={idx} className="grid grid-cols-[1fr_120px_2fr_auto] items-end gap-2">
                        <Field label="Code">
                          <input className="field-input" value={f.code} onChange={(e) => updateFinding(idx, { code: e.target.value })} placeholder="e.g. EXTINGUISHER_EXPIRED" />
                        </Field>
                        <Field label="Severity">
                          <select className="field-input" value={f.severity} onChange={(e) => updateFinding(idx, { severity: e.target.value as FindingSeverity })}>
                            {SEVERITIES.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                        </Field>
                        <Field label="Description">
                          <input className="field-input" value={f.description ?? ''} onChange={(e) => updateFinding(idx, { description: e.target.value })} />
                        </Field>
                        <button className="btn-secondary !px-2" onClick={() => removeFinding(idx)} aria-label="Remove finding">
                          ✕
                        </button>
                      </div>
                    ))}
                    <button className="btn-secondary" onClick={addFinding}>
                      + Add finding
                    </button>
                  </div>
                  {result === 'DEFECTS' && findings.filter((f) => f.code.trim()).length === 0 ? (
                    <p className="text-xs text-rose-500">At least one finding is required for a DEFECTS result.</p>
                  ) : null}
                  <Field label="Notes (optional)">
                    <textarea className="field-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
                  </Field>
                  <button
                    className={result === 'PASS' ? 'btn-success' : 'btn-danger'}
                    disabled={busy || (result === 'DEFECTS' && findings.filter((f) => f.code.trim()).length === 0)}
                    onClick={submit}
                  >
                    {busy ? 'Submitting…' : result === 'PASS' ? 'Confirm pass' : 'Confirm defects — open 30-day rectification'}
                  </button>
                </>
              ) : null}
            </div>
          ) : canAct ? null : i.status === 'BOOKED' ? (
            <RoleNotice roles={OFFICER_ROLES} />
          ) : null}

          <div className="border-t border-slate-100 pt-3 text-xs text-slate-400">Last updated {formatDateTime(i.updatedAt)}</div>
        </div>
      ) : null}
    </Modal>
  );
}
